import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, onValue } from 'firebase/database';
import { auth } from '../firebase';
import { subscribeToWebLicense } from '../services/webLicenseService';

import { doc, getFirestore, onSnapshot } from 'firebase/firestore';

const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxx-xxxx-4xxx-yxxx-xxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const useAuth = () => {
    const [user, setUser] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [isPremium, setIsPremium] = useState(false);
    const [isLicencesPremium, setIsLicencesPremium] = useState(false);
    const [isWebPremium, setIsWebPremium] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [deviceId, setDeviceId] = useState(null);
    const [webLicenseData, setWebLicenseData] = useState(null);
    const [cursoPeriodoActual, setCursoPeriodoActual] = useState('p1');
    const [cursoPeriodoAcceso, setCursoPeriodoAcceso] = useState(null);
    const [isCursoPeriodoActivo, setIsCursoPeriodoActivo] = useState(false);

    useEffect(() => {
        let storedId = localStorage.getItem('vortex_browser_id');
        if (!storedId) {
            storedId = `WEB_${generateUUID()}`;
            localStorage.setItem('vortex_browser_id', storedId);
        }
        setDeviceId(storedId);

        let unsubscribeProfile = null;

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthLoading(false);

            if (currentUser && currentUser.email) {
                const db = getDatabase();
                const firestore = getFirestore();

                // Suscripción al perfil del usuario en Firestore (Realtime)
                const userDocRef = doc(firestore, 'users', currentUser.uid);
                unsubscribeProfile = onSnapshot(userDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        setProfileData(docSnap.data());
                    } else {
                        setProfileData({});
                    }
                    setProfileLoading(false);
                }, (error) => {
                    console.error("Error fetching user profile:", error);
                    setProfileData({});
                    setProfileLoading(false);
                });

                // Suscripción a licencia de app móvil
                const checkLicense = (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        const now = Date.now();
                        // Lógica sincronizada con Android: pEnd == 0 o pEnd > now
                        // Además, somos más flexibles con isActive/isPremium (default true si no están)
                        const isActive = data.isActive !== false;
                        const isPremiumField = data.isPremium !== false;
                        const endDate = data.endDate ?? data.premiumEndDate ?? 0;
                        const isNotExpired = endDate === 0 || endDate > now;
                        
                        return isActive && isPremiumField && isNotExpired;
                    }
                    return false;
                };

                const licenseRef = ref(db, `licenses/${currentUser.uid}`);

                // Periodo actual autoritativo (config RTDB) para acceso a cursos
                const periodoActualRef = ref(db, 'config/cursoPeriodoActual');
                const periodoActualUnsub = onValue(periodoActualRef, (snap) => {
                    setCursoPeriodoActual(snap.val() || 'p1');
                });

                let licenseActive = false;
                let webLicenseActive = false;

                const updateCombinedStatus = () => {
                    setIsLicencesPremium(licenseActive);
                    setIsPremium(licenseActive || webLicenseActive);
                    setLoading(false);
                };

                const licenseUnsubscribe = onValue(licenseRef, (snapshot) => {
                    licenseActive = checkLicense(snapshot);
                    const d = snapshot.val();
                    if (d && d.cursoPeriodoAcceso) setCursoPeriodoAcceso(d.cursoPeriodoAcceso);
                    updateCombinedStatus();
                }, (error) => {
                    console.error("Error fetching license:", error);
                    licenseActive = false;
                    updateCombinedStatus();
                });

                // Suscripción de respaldo a licencesPremium para cursoPeriodoAcceso
                const licPremiumRef = ref(db, `licencesPremium/${currentUser.uid}`);
                const licPremiumUnsubscribe = onValue(licPremiumRef, (snapshot) => {
                    const d = snapshot.val();
                    if (d && d.cursoPeriodoAcceso) setCursoPeriodoAcceso(d.cursoPeriodoAcceso);
                }, () => {});

                // Suscripción a licencia web en usersweb
                const webLicenseUnsubscribe = subscribeToWebLicense(currentUser.uid, (status) => {
                    webLicenseActive = status.isValid;
                    setIsWebPremium(status.isValid);
                    setWebLicenseData(status.license);
                    updateCombinedStatus();
                });

                return () => {
                    licenseUnsubscribe();
                    licPremiumUnsubscribe();
                    periodoActualUnsub();
                    if (typeof webLicenseUnsubscribe === 'function') {
                        webLicenseUnsubscribe();
                    }
                    if (unsubscribeProfile) {
                        unsubscribeProfile();
                    }
                };
            } else {
                setIsPremium(false);
                setIsLicencesPremium(false);
                setIsWebPremium(false);
                setWebLicenseData(null);
                setProfileData(null);
                setProfileLoading(true);
                setCursoPeriodoAcceso(null);
                setIsCursoPeriodoActivo(false);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []); // Sin dependencias - solo se ejecuta una vez al montar

    // Acceso a cursos por periodo: cursoPeriodoAcceso debe coincidir con el periodo actual
    useEffect(() => {
        setIsCursoPeriodoActivo(!!cursoPeriodoAcceso && cursoPeriodoAcceso === cursoPeriodoActual);
    }, [cursoPeriodoAcceso, cursoPeriodoActual]);

    return {
        user,
        profileData,
        profileLoading,
        loading: loading || authLoading,
        authLoading,
        isPremium,
        isLicencesPremium,
        isWebPremium,
        webLicenseData,
        deviceId,
        isAuthenticated: !!user,
        isCursoPeriodoActivo,
        cursoPeriodoActual,
        cursoPeriodoAcceso
    };
};